import { useMixPanelContext } from '@aibees/shared/context';
import type { AuthToken } from '@aibees/shared/definition';
import {
  useAmplitude,
  useCustomerIO,
  useGleap,
  useLocalStorage,
  useMount,
  usePostHog,
} from '@aibees/shared/hooks';
import { userGetFullName } from '@aibees/shared/utils';
import { decode } from 'jsonwebtoken';
import { createContext, useEffect, useState } from 'react';
import { AUTH_CACHE_KEY } from '../constants';
import {
  getAuthAttemptContext,
  logoutUser,
  readUserInfoFromJwt,
} from '../utils';
import type {
  AuthContextData as ContextData,
  AuthContextProps as ContextProps,
} from './types';

export const AuthContext = createContext<ContextData>({} as ContextData);

const { Provider } = AuthContext;

export const AuthProvider = ({
  children,
  localStorageKeysToClearOnLogout,
}: ContextProps) => {
  const [customerId, setCustomerId] = useState<string>();
  const { mixpanel } = useMixPanelContext();
  const { identify: identifyAmplitude, reset: resetAmplitude } = useAmplitude();
  const {
    identify: identifyGleap,
    reset: resetGleap,
    isUserIdentified: isUserIdentifiedGleap,
  } = useGleap();
  const { identify: identifyCustomerIO, reset: resetCustomerIO } =
    useCustomerIO();
  const {
    identify: identifyPostHog,
    reset: resetPostHog,
    track: trackPostHog,
  } = usePostHog();
  const [token, setPrivateToken] = useLocalStorage<AuthToken | undefined>(
    AUTH_CACHE_KEY,
    undefined
  );
  const userFullName = userGetFullName(token?.user);

  const logOut = (noRedirect = false) => {
    mixpanel?.reset();
    resetAmplitude();
    resetGleap();
    resetCustomerIO?.();
    resetPostHog();
    logoutUser(token, noRedirect, localStorageKeysToClearOnLogout);
  };

  // CU-86e17r1cr: the trackPostHog calls below and the try/catch around
  // readUserInfoFromJwt are temporary diagnostic instrumentation. Revert this
  // function to a single `const userData = readUserInfoFromJwt(...)` + `if (userData)`
  // when the investigation closes, and drop `track: trackPostHog` from the
  // destructure above.
  const setToken = async (tokenData: Omit<AuthToken, 'user'>) => {
    trackPostHog('AUTH_SET_TOKEN_CALLED', {
      ...getAuthAttemptContext(),
      hasAccessToken: !!tokenData.access_token,
      hasRefreshToken: !!tokenData.refresh_token,
      hasIdToken: !!tokenData.id_token,
    });

    let userData: ReturnType<typeof readUserInfoFromJwt> | undefined;
    try {
      userData = readUserInfoFromJwt(tokenData.access_token);
    } catch (e) {
      trackPostHog('AUTH_SET_TOKEN_THREW', {
        ...getAuthAttemptContext(),
        stage: 'jwt_parse',
        errorMessage: e instanceof Error ? e.message : String(e),
      });
      return;
    }

    trackPostHog('AUTH_JWT_PARSED', {
      ...getAuthAttemptContext(),
      succeeded: !!userData,
      hasId: !!userData?.id,
      hasEmail: !!userData?.email,
    });

    if (userData) {
      setPrivateToken({
        ...tokenData,
        user: userData,
      });
      trackPostHog('AUTH_TOKEN_STORED', getAuthAttemptContext());

      mixpanel?.identify(userData?.id);
      mixpanel?.people.set({
        $first_name: userData?.firstName,
        $last_name: userData?.lastName,
        $email: userData?.email,
      });
      identifyAmplitude(userData?.email, {
        name: userData?.firstName + ' ' + userData?.lastName,
        email: userData?.email,
        user_id: userData?.id,
      });

      identifyPostHog(userData?.id, {
        name: userData?.firstName + ' ' + userData?.lastName,
        email: userData?.email,
        phone: userData?.attributes?.phone || null,
        company: userData?.attributes?.company || null,
        title: userData?.attributes?.title,
        site: userData?.attributes?.site,
        industry: userData?.attributes?.industry,
        alternateEmail: userData?.attributes?.alternateEmail,
        avatar: userData?.attributes?.avatar,
        lastLogin: new Date().toISOString(),
        customerId: userData?.id,
        username: userData?.username,
        roles: userData?.roles,
        realmAccessRoles: userData?.realmAccessRoles,
      });

      if (userData?.id && identifyCustomerIO) {
        identifyCustomerIO(userData.id, {
          name: userData?.firstName + ' ' + userData?.lastName,
          email: userData?.email,
          phone: userData?.attributes?.phone || null,
          company: userData?.attributes?.company || null,
          title: userData?.attributes?.title,
          site: userData?.attributes?.site,
          industry: userData?.attributes?.industry,
          alternateEmail: userData?.attributes?.alternateEmail,
          avatar: userData?.attributes?.avatar,
          lastLogin: new Date().toISOString(),
          customerId: userData?.id,
          username: userData?.username,
          roles: userData?.roles,
          realmAccessRoles: userData?.realmAccessRoles,
        });
      }

      if (!isUserIdentifiedGleap()) {
        identifyGleap(userData?.id, {
          name: userData?.firstName + ' ' + userData?.lastName,
          email: userData?.email,
          phone: userData?.attributes?.phone || null,
          value: null,
          plan: null,
          companyId: null,
          companyName: userData?.attributes?.company || null,
          customData: {
            firstName: userData?.firstName,
            lastName: userData?.lastName,
            username: userData?.username,
            roles: userData?.roles,
            realmAccessRoles: userData?.realmAccessRoles,
            title: userData?.attributes?.title,
            site: userData?.attributes?.site,
            industry: userData?.attributes?.industry,
            alternateEmail: userData?.attributes?.alternateEmail,
            avatar: userData?.attributes?.avatar,
            lastLogin: new Date().toISOString(),
            customerId: userData?.id,
          },
        });
      }
    }
  };

  const setUser: ContextData['setUser'] = (user) => {
    if (token) setPrivateToken({ ...token, user });
  };

  useMount(() => {
    if (token && !token.user) {
      const user = readUserInfoFromJwt(token.access_token);
      if (user) setUser(user);
    }
  });

  useEffect(() => {
    if (token?.access_token) {
      const cId = (decode(token?.access_token) as any)?.sub;
      setCustomerId(cId);
    }
  }, [token]);

  return (
    <Provider
      value={{
        token,
        customerId,
        setToken,
        user: token?.user,
        setUser,
        authenticated: !!token,
        loading: !token,
        logOut,
        userFullName,
      }}
    >
      {children}
    </Provider>
  );
};
